import { useEffect, useState } from "react";
import { testimonialsAPI, uploadAPI } from "../../lib/api";
import { Heart, Plus, Trash2, Edit, Star, Check, X, Image } from "lucide-react";
import { toast } from "sonner";
import { Header } from '../../components/layout/Header';

const SEGMENTOS = [
    { value: "",                       label: "Todos" },
    { value: "mulheres_com_proposito", label: "Mulheres c/ Propósito" },
    { value: "mesa_de_ester",          label: "Mesa de Ester" },
    { value: "luto_ao_recomeco",       label: "Do Luto ao Recomeço" },
    { value: "devocional",             label: "Devocional" },
    { value: "musica",                 label: "Música" },
    { value: "livro",                  label: "Livro" },
];

const EMPTY = { author_name: "", author_photo: "", segmento: "geral", content: "", rating: 5, approved: false };

function TestimonialModal({ open, onClose, onSaved, editing }) {
    const [form, setForm] = useState(EMPTY);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    useEffect(() => { setForm(editing ? { ...editing } : EMPTY); }, [editing, open]);

    const uploadPhoto = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await uploadAPI.image(fd);
            setForm(f => ({ ...f, author_photo: r.data.url }));
        } catch { toast.error("Erro ao enviar foto"); }
        finally { setUploading(false); }
    };

    const save = async () => {
        if (!form.author_name || !form.content) { toast.error("Nome e depoimento são obrigatórios"); return; }
        setSaving(true);
        try {
            if (editing) await testimonialsAPI.update(editing.id, form);
            else         await testimonialsAPI.create(form);
            toast.success(editing ? "Testemunho atualizado" : "Testemunho criado");
            onSaved(); onClose();
        } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
        finally { setSaving(false); }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-stone-900 border border-stone-700 rounded-2xl w-full max-w-md shadow-2xl">
                <div className="p-5 border-b border-stone-800 flex items-center justify-between">
                    <h2 className="font-bold text-stone-100">{editing ? "Editar" : "Novo"} Testemunho</h2>
                    <button onClick={onClose}><X className="w-4 h-4 text-stone-500" /></button>
                </div>
                <div className="p-5 space-y-3">
                    {/* Foto do autor */}
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-stone-800 overflow-hidden flex-shrink-0">
                            {form.author_photo
                                ? <img src={form.author_photo} alt="" className="w-full h-full object-cover" />
                                : <div className="w-full h-full flex items-center justify-center text-stone-600"><Image className="w-5 h-5" /></div>
                            }
                        </div>
                        <label className="text-xs text-amber-500 hover:text-amber-400 cursor-pointer">
                            {uploading ? "Enviando..." : "Enviar foto"}
                            <input type="file" className="hidden" accept="image/*" onChange={uploadPhoto} />
                        </label>
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Nome *</label>
                        <input className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                            value={form.author_name} onChange={e => setForm(f => ({...f, author_name: e.target.value}))} />
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Jornada</label>
                        <select className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600"
                            value={form.segmento} onChange={e => setForm(f => ({...f, segmento: e.target.value}))}>
                            {SEGMENTOS.filter(s => s.value).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Avaliação</label>
                        <div className="flex gap-1">
                            {[1,2,3,4,5].map(n => (
                                <button key={n} onClick={() => setForm(f => ({...f, rating: n}))}>
                                    <Star className={`w-5 h-5 ${n <= form.rating ? "text-amber-400 fill-amber-400" : "text-stone-600"}`} />
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-stone-400 block mb-1">Depoimento *</label>
                        <textarea rows={4} className="w-full bg-stone-800 border border-stone-700 rounded-lg px-3 py-2 text-sm text-stone-100 focus:outline-none focus:border-amber-600 resize-none"
                            value={form.content} onChange={e => setForm(f => ({...f, content: e.target.value}))}
                            placeholder="Escreva o depoimento..." />
                    </div>
                    <label className="flex items-center gap-2 text-sm text-stone-300 cursor-pointer">
                        <input type="checkbox" checked={form.approved} onChange={e => setForm(f => ({...f, approved: e.target.checked}))} />
                        Aprovado (visível no site)
                    </label>
                </div>
                <div className="p-5 border-t border-stone-800 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-stone-400 hover:text-stone-200">Cancelar</button>
                    <button onClick={save} disabled={saving}
                        className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-white text-sm rounded-lg disabled:opacity-50 transition-colors">
                        {saving ? "Salvando..." : "Salvar"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function TestimonialsAdmin() {
    const [testimonials, setTestimonials] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState(false);
    const [editing, setEditing] = useState(null);
    const [segFilter, setSegFilter] = useState("");

    const load = async () => {
        setLoading(true);
        try { const r = await testimonialsAPI.list(); setTestimonials(r.data || []); }
        catch { toast.error("Erro ao carregar testemunhos"); }
        finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    const toggleApprove = async (t) => {
        try {
            await testimonialsAPI.update(t.id, { ...t, approved: !t.approved });
            toast.success(t.approved ? "Ocultado do site" : "Aprovado para o site");
            load();
        } catch { toast.error("Erro"); }
    };

    const del = async (id) => {
        if (!window.confirm("Excluir testemunho?")) return;
        try { await testimonialsAPI.delete(id); toast.success("Excluído"); load(); }
        catch { toast.error("Erro"); }
    };

    const filtered = testimonials.filter(t => !segFilter || t.segmento === segFilter);

    return (
        <>
        <Header title="Testemunhos" subtitle={`${testimonials.length} depoimentos · ${testimonials.filter(t => t.approved).length} aprovados`} actions={<button onClick={() => setModal(true)} className="flex items-center gap-2 bg-amber-700 hover:bg-amber-600 text-white text-sm px-4 py-2 rounded-lg transition-colors"><Plus className="w-4 h-4" /> Novo Testemunho</button>} />
        <div className="p-6 space-y-5">
            <TestimonialModal open={modal} onClose={() => { setModal(false); setEditing(null); }}
                onSaved={load} editing={editing} />


            {/* Filtro por segmento */}
            <div className="flex gap-1.5 flex-wrap">
                {SEGMENTOS.map(s => (
                    <button key={s.value} onClick={() => setSegFilter(s.value)}
                        className={`text-xs px-3 py-1.5 rounded-lg border transition-colors ${segFilter === s.value ? "bg-amber-700 border-amber-600 text-white" : "border-stone-700 text-stone-400 hover:text-stone-200"}`}>
                        {s.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-stone-500 text-sm text-center py-16">Carregando...</div>
            ) : filtered.length === 0 ? (
                <div className="bg-stone-900 border border-stone-800 rounded-xl p-12 text-center">
                    <Heart className="w-10 h-10 text-stone-700 mx-auto mb-3" />
                    <p className="text-stone-400 text-sm">Nenhum testemunho ainda.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(t => (
                        <div key={t.id} className={`bg-stone-900 border rounded-xl p-4 transition-all hover:border-stone-700 ${t.approved ? "border-stone-700" : "border-stone-800 opacity-70"}`}>
                            <div className="flex items-start gap-3 mb-3">
                                <div className="w-10 h-10 rounded-full bg-stone-800 flex-shrink-0 overflow-hidden">
                                    {t.author_photo
                                        ? <img src={t.author_photo} alt="" className="w-full h-full object-cover" />
                                        : <div className="w-full h-full flex items-center justify-center text-amber-500 font-bold">{t.author_name?.[0]}</div>
                                    }
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-stone-200">{t.author_name}</p>
                                    <div className="flex items-center gap-1 mt-0.5">
                                        {[1,2,3,4,5].map(n => (
                                            <Star key={n} className={`w-3 h-3 ${n <= (t.rating || 5) ? "text-amber-400 fill-amber-400" : "text-stone-700"}`} />
                                        ))}
                                    </div>
                                </div>
                                <span className={`text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 ${t.approved ? "bg-green-900/30 text-green-300" : "bg-stone-800 text-stone-500"}`}>
                                    {t.approved ? "✓" : "—"}
                                </span>
                            </div>
                            <p className="text-xs text-stone-400 leading-relaxed line-clamp-3">{t.content}</p>
                            <div className="flex items-center gap-2 mt-3 pt-3 border-t border-stone-800">
                                <button onClick={() => toggleApprove(t)}
                                    className={`flex-1 text-xs py-1.5 rounded-lg border transition-colors flex items-center justify-center gap-1 ${t.approved ? "border-stone-700 text-stone-400 hover:text-red-400" : "border-green-700 text-green-400 hover:bg-green-900/20"}`}>
                                    {t.approved ? <><X className="w-3 h-3" /> Ocultar</> : <><Check className="w-3 h-3" /> Aprovar</>}
                                </button>
                                <button onClick={() => { setEditing(t); setModal(true); }}
                                    className="p-1.5 text-stone-500 hover:text-stone-200 hover:bg-stone-800 rounded-lg transition-colors">
                                    <Edit className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => del(t.id)}
                                    className="p-1.5 text-stone-500 hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors">
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
        </>
    );
}
